/**
 * Design tokens for the Evaluate / Execute drilldown paths.
 *
 * Values are fixed by the design handoff (claude_code_files/design_handoff_drilldown_redesign).
 * Verdict and status colors are applied as inline styles rather than Tailwind classes because
 * the banner tints derive from them at alpha (see VERDICT_BANNER_ALPHA).
 */

export type VerdictKey = 'green' | 'cond' | 'hold';

export interface VerdictToken {
  key: VerdictKey;
  /** Full label, used on the large pill in the decision banner. */
  label: string;
  /** Short label, used on list rows and compare cells. */
  short: string;
  /** Action verb the Proceed CTA adapts to. */
  verb: string;
  bg: string;
  fg: string;
  dot: string;
}

export const VERDICTS: Record<VerdictKey, VerdictToken> = {
  green: { key: 'green', label: 'Green Light · Proceed', short: 'Green Light', verb: 'Proceed', bg: '#dcfce7', fg: '#15803d', dot: '#16a34a' },
  cond: { key: 'cond', label: 'Conditional · Validate First', short: 'Conditional', verb: 'Validate', bg: '#fef3c7', fg: '#b45309', dot: '#d97706' },
  hold: { key: 'hold', label: 'Hold · Revisit Later', short: 'Hold', verb: 'Defer', bg: '#e2e8f0', fg: '#475569', dot: '#64748b' },
};

/** Banner tint: border is the dot at 55% alpha, background the bg at 66%. */
export const VERDICT_BANNER_ALPHA = { border: '55', background: '66' } as const;

export type ExecStatus = 'Active' | 'CFO Review' | 'Activation Required' | 'Approval Pending';

export const EXEC_STATUSES: Record<ExecStatus, { bg: string; fg: string; dot: string }> = {
  'Active': { bg: '#ecfdf5', fg: '#047857', dot: '#10b981' },
  'CFO Review': { bg: '#fffbeb', fg: '#b45309', dot: '#f59e0b' },
  'Activation Required': { bg: '#eef2ff', fg: '#4338ca', dot: '#6366f1' },
  'Approval Pending': { bg: '#f1f5f9', fg: '#475569', dot: '#94a3b8' },
};

export type MilestoneStatus = 'Complete' | 'In Progress' | 'Not Started';

export const MILESTONE_STATUS_COLORS: Record<MilestoneStatus, string> = {
  'Complete': '#059669',
  'In Progress': '#2563eb',
  'Not Started': '#94a3b8',
};

/** Severity dot classes. 'Moderate' is the Execute-path spelling of 'Medium'. */
export const SEVERITY_DOT: Record<string, string> = {
  Critical: 'bg-red-600',
  High: 'bg-orange-500',
  Medium: 'bg-amber-500',
  Moderate: 'bg-amber-500',
  Low: 'bg-emerald-500',
};
